import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  GAME_CONFIG,
  RESEARCHES,
  CONTROLS,
  formatHotkeyLabel,
  ITEMS,
} from './constants';
import { useUIState, useMapState } from './contexts/GameStateManager';
import { getGameEngine } from './GameEngine';
import { DebugState } from './hooks/useDebugState';
import { useToast } from './hooks/useToast';
import {
  getActiveResearchId,
  getNextResearchRequirements,
  getResearchProgress,
} from './ResearchSystem';
import { RENDERER_MODE_LABELS } from './types/Renderer';
import { generateTile } from './WorldGeneration';

// TopMenu props interface
interface TopMenuProps {
  debugState: DebugState;
  toggleDebugDetails: () => void;
}

const TopMenu: React.FC<TopMenuProps> = ({
  debugState,
  toggleDebugDetails,
}) => {
  // Use contexts for state management
  const uiState = useUIState();
  const mapState = useMapState();
  const { showSuccess, showError } = useToast();

  // Destructure values from contexts
  const {
    helpMenuOpen,
    setHelpMenuOpen,
    setSaveMenuOpen,
    setLeftPanelOpen,
    setAchievementMenuOpen,
    researchMenuOpen,
    setResearchMenuOpen,
    prestigeMenuOpen,
    setPrestigeMenuOpen,
    productionMenuOpen,
    setProductionMenuOpen,
    inventoryOpen,
    setInventoryOpen,
    inventoryPulseId,
    buildMode,
    exitBuildMode,
    setBuildMenuState,
  } = uiState;

  const {
    prestigePoints,
    prestigeCount,
    zoomLevel,
    playerFacing,
    getBiome,
    getTargetPosition,
    gameMap,
    buildingLayer,
    beltContentLayer,
    rejectedItemsLayer,
    researchState,
    tickSpeedMultiplier,
    cycleTickSpeedMultiplier,
    hasEnabledDebug,
    rendererMode,
    toggleRendererMode,
    quickBeltPreview,
    confirmQuickBeltPlacement,
    cancelQuickBeltPlacement,
  } = mapState;

  const selectedResearchId = getActiveResearchId(researchState);
  const hasPrestiged = (prestigeCount ?? 0) > 0 || (prestigePoints ?? 0) > 0;
  const currentResearch = selectedResearchId
    ? RESEARCHES[selectedResearchId]
    : undefined;
  const researchRequirements = selectedResearchId
    ? getNextResearchRequirements(researchState, selectedResearchId)
    : [];
  const researchTotalRequired = researchRequirements.reduce(
    (sum, requirement) => sum + requirement.amount,
    0
  );
  const totalResearchProgress = selectedResearchId
    ? getResearchProgress(researchState, selectedResearchId)
    : 0;
  const cappedResearchProgress =
    researchTotalRequired > 0
      ? Math.min(totalResearchProgress, researchTotalRequired)
      : totalResearchProgress;
  const researchProgressPercent =
    researchTotalRequired > 0
      ? Math.min(cappedResearchProgress / researchTotalRequired, 1)
      : 0;
  const showResearchProgressIndicator =
    Boolean(selectedResearchId) && researchTotalRequired > 0;
  const researchProgressDisplay = Math.round(researchProgressPercent * 100);
  const researchButtonTooltip = selectedResearchId
    ? `${currentResearch?.name ?? selectedResearchId}: ${researchProgressDisplay}%`
    : 'Research Menu (F2)';
  const prestigeButtonTooltip = 'Prestige Upgrades';
  const buildMenuHotkeyLabel = formatHotkeyLabel(CONTROLS.BUILD_MENU);
  const takeActionHotkeyLabel = formatHotkeyLabel(
    CONTROLS.TAKE_ACTION.map(key => (key === ' ' ? 'Space' : key))
  );
  const multiPlacementHotkeyLabel = formatHotkeyLabel(
    CONTROLS.MULTIPLE_PLACEMENT
  );
  const deconstructHotkeyLabel = formatHotkeyLabel(CONTROLS.BUILD_DECONSTRUCT);
  const collectResourceHotkeyLabel = formatHotkeyLabel(
    CONTROLS.COLLECT_RESOURCE
  );
  const cancelHotkeyLabel = formatHotkeyLabel(CONTROLS.CANCEL);
  const inventoryHotkeyLabel = formatHotkeyLabel(CONTROLS.INVENTORY);
  const productionHotkeyLabel = formatHotkeyLabel(CONTROLS.PRODUCTION_MENU);
  const saveMenuHotkeyLabel = formatHotkeyLabel(CONTROLS.SAVE_MENU);
  const quickSaveHotkeyLabel = formatHotkeyLabel(CONTROLS.SAVE_GAME);
  const achievementHotkeyLabel = formatHotkeyLabel(CONTROLS.ACHIEVEMENT_MENU);
  const debugMenuHotkeyLabel = formatHotkeyLabel(CONTROLS.DEBUG_MENU);
  const helpHotkeyLabel = formatHotkeyLabel(CONTROLS.HELP_MENU);
  const researchMenuHotkeyLabel = formatHotkeyLabel(CONTROLS.RESEARCH_MENU);
  const rendererButtonTooltip =
    rendererMode === 'three'
      ? 'Switch to the classic ASCII renderer'
      : 'Switch to the 3D renderer';
  const rendererBadgeLabel = rendererMode === 'three' ? '3D' : '2D';

  const [isInventoryPulsing, setIsInventoryPulsing] = useState(false);
  const [activeInventoryPulseId, setActiveInventoryPulseId] = useState<
    number | null
  >(null);

  const showQuickBeltConfirmation = quickBeltPreview?.status === 'confirm';
  const quickBeltSegmentCount = quickBeltPreview?.segments.length ?? 0;
  const quickBeltCostLabel = useMemo(() => {
    if (!quickBeltPreview || quickBeltPreview.cost.length === 0) {
      return 'None';
    }

    return quickBeltPreview.cost
      .map(requirement => {
        const itemConfig = Object.values(ITEMS).find(
          item => item.type === requirement.item
        );
        const itemName = itemConfig?.name ?? requirement.item;
        const character = itemConfig?.character ?? '?';
        return `${requirement.amount}x ${character} ${itemName}`;
      })
      .join(', ');
  }, [quickBeltPreview]);
  const quickBeltInvalidReason = quickBeltPreview?.invalidReason;

  const handleConfirmQuickBelt = React.useCallback(() => {
    const result = confirmQuickBeltPlacement();
    if (result.success) {
      if (result.placedCount && result.placedCount > 0) {
        showSuccess(`Placed ${result.placedCount} belts.`);
      } else {
        showSuccess('Placed belts.');
      }
      return;
    }

    if (result.error) {
      showError(result.error);
    }
  }, [confirmQuickBeltPlacement, showError, showSuccess]);

  const handleCancelQuickBelt = React.useCallback(() => {
    cancelQuickBeltPlacement();
  }, [cancelQuickBeltPlacement]);

  useEffect(() => {
    if (!inventoryPulseId) {
      return;
    }

    setIsInventoryPulsing(true);
    setActiveInventoryPulseId(inventoryPulseId);
    const timeout = window.setTimeout(() => {
      setIsInventoryPulsing(false);
      setActiveInventoryPulseId(null);
    }, 900);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [inventoryPulseId]);

  // Calculate target position for debug display
  const targetPos = getTargetPosition ? getTargetPosition() : null;
  const handleCycleSpeed = () => {
    if (!hasEnabledDebug) {
      return;
    }

    cycleTickSpeedMultiplier();
  };

  const handleExitBuildMode = useCallback(() => {
    setBuildMenuState({
      selectedBuilding: null,
      buildMenuOpen: false,
    });
    exitBuildMode();
  }, [exitBuildMode, setBuildMenuState]);
  return (
    <>
      {/* Top-right menu */}
      <div className='top-menu'>
        {/* Menu buttons at the top */}
        <div className='menu-buttons'>
          <div className='menu-button-wrapper'>
            <button
              className='menu-button'
              onClick={() => setHelpMenuOpen(!helpMenuOpen)}
              title='Help & Controls'
            >
              <span className='menu-button-icon'>❓</span>
            </button>
          </div>
          <div className='menu-button-wrapper'>
            <button
              className={`menu-button ${
                inventoryOpen ? 'active' : ''
              } ${isInventoryPulsing ? 'inventory-pulsing' : ''}`}
              onClick={() => setInventoryOpen(prev => !prev)}
              title={`Inventory (${inventoryHotkeyLabel})`}
            >
              {isInventoryPulsing && (
                <span
                  key={activeInventoryPulseId ?? undefined}
                  className='inventory-pulse-indicator'
                  aria-hidden='true'
                />
              )}
              <span className='menu-button-icon'>🎒</span>
            </button>
          </div>
          <div className='menu-button-wrapper'>
            <button
              className={`menu-button ${researchMenuOpen ? 'active' : ''}`}
              onClick={() => setResearchMenuOpen(!researchMenuOpen)}
              title={researchButtonTooltip}
            >
              {showResearchProgressIndicator && (
                <span className='menu-button-progress-track' aria-hidden='true'>
                  <span
                    className='menu-button-progress-fill'
                    style={{ width: `${researchProgressDisplay}%` }}
                  />
                </span>
              )}
              <span className='menu-button-icon'>🔬</span>
            </button>
          </div>
          {hasPrestiged && (
            <div className='menu-button-wrapper'>
              <button
                className={`menu-button ${prestigeMenuOpen ? 'active' : ''}`}
                onClick={() => setPrestigeMenuOpen(!prestigeMenuOpen)}
                title={prestigeButtonTooltip}
                aria-label='Prestige Upgrades'
                aria-pressed={prestigeMenuOpen}
              >
                <span className='menu-button-icon'>🌟</span>
              </button>
            </div>
          )}
          <div className='menu-button-wrapper'>
            <button
              className={`menu-button ${productionMenuOpen ? 'active' : ''}`}
              onClick={() => setProductionMenuOpen(!productionMenuOpen)}
              title={`Production Analytics (${productionHotkeyLabel})`}
              aria-pressed={productionMenuOpen}
            >
              <span className='menu-button-icon'>📈</span>
            </button>
          </div>
          {hasEnabledDebug && (
            <div className='menu-button-wrapper'>
              <button
                className='menu-button'
                onClick={handleCycleSpeed}
                title={`Game speed: ${tickSpeedMultiplier}x (click to cycle)`}
              >
                <span className='menu-button-icon'>⏱️</span>
                <span className='menu-button-badge'>
                  {tickSpeedMultiplier}x
                </span>
              </button>
            </div>
          )}
          <div className='menu-button-wrapper'>
            <button
              className={`menu-button ${
                rendererMode === 'three' ? 'active' : ''
              }`}
              onClick={toggleRendererMode}
              title={`${rendererButtonTooltip} (${RENDERER_MODE_LABELS[rendererMode]})`}
              aria-label={`Renderer mode: ${RENDERER_MODE_LABELS[rendererMode]}`}
              aria-pressed={rendererMode === 'three'}
            >
              <span className='menu-button-icon'>
                {rendererMode === 'three' ? '🧊' : '🗔'}
              </span>
              <span className='menu-button-badge'>{rendererBadgeLabel}</span>
            </button>
          </div>
          <div className='menu-button-wrapper'>
            <button
              className='menu-button'
              onClick={() => setSaveMenuOpen(true)}
              title='Save Game'
            >
              <span className='menu-button-icon'>💾</span>
            </button>
          </div>
          <div className='menu-button-wrapper'>
            <button
              className='menu-button'
              onClick={() => setAchievementMenuOpen(true)}
              title='Achievements (F9)'
            >
              <span className='menu-button-icon'>🏆</span>
            </button>
          </div>
          <div className='menu-button-wrapper'>
            <button
              className='menu-button'
              onClick={() => setLeftPanelOpen(true)}
              title='Toggle Debug Panel'
            >
              <span className='menu-button-icon'>🔧</span>
            </button>
          </div>
          <div className='menu-button-wrapper'>
            <button
              className={`menu-button ${debugState.showDebugDetails ? 'active' : ''}`}
              onClick={toggleDebugDetails}
              title='Toggle Debug Details'
            >
              <span className='menu-button-icon'>📊</span>
            </button>
          </div>
        </div>
        {showQuickBeltConfirmation && (
          <div
            className={`quick-belt-confirmation${
              quickBeltPreview?.isValid
                ? ''
                : ' quick-belt-confirmation-invalid'
            }`}
          >
            <div className='quick-belt-summary'>
              <span className='quick-belt-title'>Quick belt placement</span>
              <span className='quick-belt-count'>
                {quickBeltSegmentCount} tile
                {quickBeltSegmentCount === 1 ? '' : 's'}
              </span>
              <span className='quick-belt-cost'>
                Cost: {quickBeltCostLabel}
              </span>
            </div>
            {!quickBeltPreview?.isValid && quickBeltInvalidReason && (
              <div className='quick-belt-error-message'>
                {quickBeltInvalidReason}
              </div>
            )}
            <div className='quick-belt-actions'>
              <button
                className='quick-belt-button confirm'
                onClick={handleConfirmQuickBelt}
                disabled={!quickBeltPreview?.isValid}
              >
                Place belts
              </button>
              <button
                className='quick-belt-button cancel'
                onClick={handleCancelQuickBelt}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {buildMode && (
          <div className='mobile-build-mode-controls'>
            <span className='mobile-build-mode-label'>Build mode active</span>
            <button
              type='button'
              className='mobile-build-mode-exit-button'
              onClick={handleExitBuildMode}
            >
              Exit build mode
            </button>
          </div>
        )}

        {/* Game info below buttons */}
        {hasPrestiged && (
          <div className='game-info' style={{ width: '100%' }}>
            <span className='prestige-info'>
              Prestige: {prestigePoints} (Runs: {prestigeCount})
            </span>
          </div>
        )}

        {/* Debug details for target square - inside menu but below other content */}
        {debugState.showDebugDetails && targetPos && (
          <div
            className='debug-details'
            style={{
              marginTop: '15px',
              paddingTop: '15px',
              borderTop: '1px solid #4ecdc4',
              fontSize: '0.8em',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <div>
              <strong>Target Square Details:</strong>
            </div>
            <div>
              Position: ({targetPos.x}, {targetPos.y}) [{playerFacing}]
            </div>

            {/* Original world generation tile */}
            <div>Original Tile: {generateTile(targetPos.x, targetPos.y)}</div>

            {/* Current terrain layer */}
            <div>
              Terrain:{' '}
              {gameMap[`${targetPos.x},${targetPos.y}`] ||
                generateTile(targetPos.x, targetPos.y)}
            </div>
            <div>Biome: {getBiome(targetPos.x, targetPos.y).name}</div>

            {/* Building layer */}
            <div>
              Building:{' '}
              {buildingLayer[`${targetPos.x},${targetPos.y}`] || 'none'}
            </div>

            {/* Belt content */}
            {(() => {
              const beltItems =
                beltContentLayer[`${targetPos.x},${targetPos.y}`];
              return beltItems && beltItems.length > 0 ? (
                <div>
                  Belt Items:{' '}
                  {beltItems
                    .map(item => `${item.item}(${item.progress.toFixed(2)})`)
                    .join(', ')}
                </div>
              ) : null;
            })()}

            {/* Rejected items */}
            {(() => {
              const rejectedItem =
                rejectedItemsLayer[`${targetPos.x},${targetPos.y}`];
              return rejectedItem ? (
                <div>Rejected: {rejectedItem.itemType}</div>
              ) : null;
            })()}

            {/* Entities */}
            {(() => {
              const gameEngine = getGameEngine();
              const entities = gameEngine
                .getEntityManager()
                .getEntitiesAt(targetPos);
              return entities.length > 0 ? (
                <div>
                  Entities ({entities.length}):
                  {entities.map(entity => (
                    <div
                      key={entity.id}
                      style={{ marginLeft: '10px', fontSize: '0.8em' }}
                    >
                      - {entity.type} [{entity.id}]
                    </div>
                  ))}
                </div>
              ) : (
                <div>Entities: none</div>
              );
            })()}

            {/* Collision System Info */}
            {(() => {
              const gameEngine = getGameEngine();
              const entityManager = gameEngine.getEntityManager();
              const collisionService = gameEngine.getCollisionService();

              return (
                <div
                  style={{
                    marginTop: '10px',
                    borderTop: '1px solid #666',
                    paddingTop: '5px',
                    maxWidth: '250px',
                    maxHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    overflow: 'auto',
                    border: '1px solid #444',
                    padding: '4px',
                    borderRadius: '4px',
                    fontSize: '0.75em',
                  }}
                >
                  <div>
                    <strong>Collision System:</strong>
                  </div>
                  <div>Total Entities: {entityManager.getEntityCount()}</div>
                  <div>
                    Entities at Position:{' '}
                    {entityManager.getEntitiesAt(targetPos).length}
                  </div>
                  <div>
                    CollisionService Dirty:{' '}
                    {collisionService.isDirtyState() ? 'true' : 'false'}
                  </div>
                  <div>
                    Collision Stats:{' '}
                    {JSON.stringify(collisionService.getStats())}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Help menu overlay */}
      {helpMenuOpen && (
        <div className='help-menu'>
          <div className='help-menu-content'>
            <div className='help-header'>
              <h3>FactoryGPT - Help & Controls</h3>
              <button
                className='close-button'
                onClick={() => setHelpMenuOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className='help-sections'>
              <div className='help-section'>
                <h4>Movement</h4>
                <p>
                  <kbd>WASD</kbd> or <kbd>Arrow Keys</kbd> - Move your character
                  ({GAME_CONFIG.PLAYER_CHARACTER})
                </p>
                <p>
                  <kbd>{takeActionHotkeyLabel}</kbd> - Interact with the tile in
                  front of you (open mining menu on resource nodes, configure
                  nearby buildings)
                </p>
                <p>
                  <kbd>{collectResourceHotkeyLabel}</kbd> - Collect nearby belt
                  and container items
                </p>
              </div>

              <div className='help-section'>
                <h4>Building</h4>
                <p>
                  <kbd>{buildMenuHotkeyLabel}</kbd> - Open build menu
                </p>
                <p>
                  <kbd>Click building</kbd> - Select building and enter build
                  mode
                </p>
                <p>
                  <kbd>{takeActionHotkeyLabel}</kbd> - Place building in facing
                  direction
                </p>
                <p>
                  <kbd>{multiPlacementHotkeyLabel}</kbd> - Hold to stay in build
                  mode after placing (for multiple buildings)
                </p>
                <p>
                  <kbd>{takeActionHotkeyLabel}</kbd> on resource node -
                  Quick-open mining menu for that node
                </p>
                <p>
                  <kbd>{deconstructHotkeyLabel}</kbd> - Deconstruct building in
                  facing direction (full refund)
                </p>
                <p>
                  <kbd>{cancelHotkeyLabel}</kbd> - Exit build mode / Clear
                  selection
                </p>
              </div>

              <div className='help-section'>
                <h4>Camera</h4>
                <p>
                  <kbd>+/-</kbd> - Zoom in/out
                </p>
                <p>
                  <kbd>0</kbd> - Reset zoom (Current:{' '}
                  {Math.round(zoomLevel * 100)}%)
                </p>
              </div>

              <div className='help-section'>
                <h4>Menus & Systems</h4>
                <p>
                  <kbd>{inventoryHotkeyLabel}</kbd> - Inventory
                </p>
                <p>
                  <kbd>{researchMenuHotkeyLabel}</kbd> - Research menu
                </p>
                <p>
                  <kbd>{productionHotkeyLabel}</kbd> - Production analytics
                </p>
                <p>
                  <kbd>{achievementHotkeyLabel}</kbd> - Achievement menu
                </p>
                <p>
                  <kbd>{debugMenuHotkeyLabel}</kbd> - Debug menu
                </p>
                <p>
                  <kbd>{saveMenuHotkeyLabel}</kbd> - Save menu
                </p>
                <p>
                  <kbd>{quickSaveHotkeyLabel}</kbd> - Quick save
                </p>
                <p>
                  <kbd>{helpHotkeyLabel}</kbd> - Toggle help menu
                </p>
                <p>⏱️ button - Cycle game speed (1x / 2x / 4x)</p>
              </div>
            </div>

            <div className='help-footer'>
              <p>
                World size: {GAME_CONFIG.WORLD_WIDTH} ×{' '}
                {GAME_CONFIG.WORLD_HEIGHT}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopMenu;
